const REACH_V3 = {
    Network: "from anywhere on the network",
    Adjacent: "from an adjacent network segment or a restricted administrative network rather than the internet at large",
    // The lure branch of Local ("relies on User Interaction by another person")
    // is only in the vector when user interaction is required; with none, the
    // clause would contradict the victim clause that follows it.
    Local: "either with local access to the host or by getting one of your users to open something",
    Physical: "by physically handling the device",
};
const REACH_LOCAL_ONLY = "with local access to the host";
const REACH_V2 = {
    Network: "from anywhere on the network",
    // v2 adjacency is the broadcast/collision domain, narrower than v3's logical
    // adjacency, and v2 has no Physical metric — local covers both.
    Adjacent: "from the same broadcast or collision domain as the host",
    Local: "with physical access or a local account on the host",
};
const COMPLEXITY_V2 = {
    Medium: "where the configuration is non-default or the attacker must first gather information",
    High: "only in a rarely-seen configuration or from a position the attacker must already hold",
};
const COMPLEXITY = "only under conditions beyond the attacker's control, after target-specific preparation rather than at will";
const PRECONDITIONS_V4 = "and only where a specific deployment, timing or network-position condition is met";
const PRIVILEGES = {
    None: "with no account",
    Low: "with an ordinary user account",
    High: "with administrative privileges",
};
const AUTHENTICATION = {
    None: "with no account",
    Single: "while logged in to the system",
    Multiple: "after authenticating more than once",
};
const VICTIM = {
    None: "and no action from your users",
    Required: "and requires some action by one of your users",
    Passive: "and depends on a user unknowingly interacting with it",
    Active: "and requires a user to be led into a deliberate action or into actively overriding a protection",
};
// Impact fragments MUST NOT contain a comma: they are strung into "a, b and c",
// so an internal comma makes the final "and" attach to the wrong clause.
//
// The total-loss fragments render each spec's "total loss" branch only. Every
// spec also reaches its top magnitude through a partial-but-serious loss, and
// the vector alone cannot tell the two apart, so those findings read as total.
const IMPACT = {
    confidentiality: {
        High: "read all restricted data",
        Complete: "read all restricted data",
        Low: "read some restricted data",
        Partial: "read some restricted data",
    },
    integrity: {
        High: "modify any restricted data",
        Complete: "modify any restricted data",
        Low: "make limited changes without controlling the consequence",
        Partial: "make limited changes without controlling the consequence",
    },
    availability: {
        High: "take the service down",
        Complete: "take the service down",
        Low: "degrade performance or cause interruptions",
        Partial: "degrade performance or cause interruptions",
    },
};
const NO_IMPACT = "No direct impact on confidentiality, integrity, or availability.";
const CROSSES_AUTHORITY = "The exploit reaches past the vulnerable component's own security boundary into resources managed by a different security authority.";
const carriesInto = (impacts) => `The attack carries into systems beyond the vulnerable one, where an attacker can ${joinFragments(impacts)}.`;
const DIMENSIONS = ["confidentiality", "integrity", "availability"];
const impactClauses = (get, labelSuffix = "") => {
    const values = DIMENSIONS.map((dimension) => get(`${dimension} impact${labelSuffix}`));
    if (values.every((value) => value === undefined))
        return undefined;
    return DIMENSIONS.map((dimension, index) => { var _a; return IMPACT[dimension][(_a = values[index]) !== null && _a !== void 0 ? _a : ""]; }).filter((fragment) => fragment !== undefined);
};
const v2Clauses = (get) => { var _a, _b, _c; return ({
    reach: REACH_V2[(_a = get("access vector")) !== null && _a !== void 0 ? _a : ""],
    complexity: COMPLEXITY_V2[(_b = get("access complexity")) !== null && _b !== void 0 ? _b : ""],
    access: AUTHENTICATION[(_c = get("authentication")) !== null && _c !== void 0 ? _c : ""],
    // v2 has no user interaction metric and no scope metric, so the story stops
    // at authentication and never says how far the exploit spreads.
    impacts: impactClauses(get),
}); };
const v3Clauses = (get) => {
    var _a;
    const interaction = get("user interaction");
    const vector = get("attack vector");
    return {
        reach: vector === "Local" && interaction === "None"
            ? REACH_LOCAL_ONLY
            : REACH_V3[vector !== null && vector !== void 0 ? vector : ""],
        complexity: get("attack complexity") === "High" ? COMPLEXITY : undefined,
        access: PRIVILEGES[(_a = get("privileges required")) !== null && _a !== void 0 ? _a : ""],
        victim: VICTIM[interaction !== null && interaction !== void 0 ? interaction : ""],
        impacts: impactClauses(get),
        // Changed scope is a security-authority crossing, not a network one, and
        // the spec lets the impact metrics describe whichever component suffers
        // worse — so the clause names no impacts.
        spread: get("scope") === "Changed" ? CROSSES_AUTHORITY : undefined,
    };
};
const v4Clauses = (get) => {
    var _a;
    const interaction = get("user interaction");
    const vector = get("attack vector");
    const subsequent = impactClauses(get, " (subsequent system)");
    return {
        reach: vector === "Local" && interaction === "None"
            ? REACH_LOCAL_ONLY
            : REACH_V3[vector !== null && vector !== void 0 ? vector : ""],
        complexity: get("attack complexity") === "High" ? COMPLEXITY : undefined,
        // v4 split the ground v3 folded into attack complexity out into its own
        // metric, which is why only v4 can state this clause.
        preconditions: get("attack requirements") === "Present" ? PRECONDITIONS_V4 : undefined,
        access: PRIVILEGES[(_a = get("privileges required")) !== null && _a !== void 0 ? _a : ""],
        victim: VICTIM[interaction !== null && interaction !== void 0 ? interaction : ""],
        impacts: impactClauses(get, " (vulnerable system)"),
        // Scope is gone in v4: the subsequent-system group is what says the exploit
        // spreads, and unlike v3's scope bit it can name the impacts.
        spread: (subsequent === null || subsequent === void 0 ? void 0 : subsequent.length) ? carriesInto(subsequent) : undefined,
    };
};
function joinFragments(fragments) {
    if (fragments.length < 2)
        return fragments.join("");
    return `${fragments.slice(0, -1).join(", ")} and ${fragments[fragments.length - 1]}`;
}
function compose({ reach, complexity, preconditions, access, victim, impacts, spread, }) {
    const sentences = [];
    if (reach) {
        const lead = [reach, complexity, preconditions].filter(Boolean).join(" ");
        const tail = [access, victim].filter(Boolean).join(", ");
        sentences.push(`Exploitable ${lead}${tail ? `, ${tail}` : ""}.`);
    }
    if (impacts) {
        sentences.push(impacts.length ? `An attacker can ${joinFragments(impacts)}.` : NO_IMPACT);
    }
    if (spread)
        sentences.push(spread);
    return sentences;
}
// Labels arrive spelled by the backend ("Attack Vector"), so they are matched
// case-insensitively rather than by exact string.
const metricLookup = (cvss) => {
    const metrics = new Map(cvss.metrics.map(([label, value]) => [label.toLowerCase(), value]));
    return (label) => metrics.get(label);
};
const specGroup = ({ version }) => {
    if (version >= 4)
        return "v4";
    if (version >= 3)
        return "v3";
    return "v2";
};
const CLAUSES_BY_GROUP = {
    v2: v2Clauses,
    v3: v3Clauses,
    v4: v4Clauses,
};
/** Retells a CVSS vector as prose: how the vulnerability is reached, what an
 * attacker gets, and how far it spreads. Returns one sentence per slot that the
 * vector actually fills, and an empty array when none of its metrics are known
 * to the version's tables. */
export function composeCvssStory(cvss) {
    return compose(CLAUSES_BY_GROUP[specGroup(cvss)](metricLookup(cvss)));
}
// CVSS abbreviates each metric VALUE, not each metric, so one word-to-letter
// table serves every metric of every version without collisions.
const VALUE_CODES = {
    Network: "N",
    Adjacent: "A",
    Local: "L",
    Physical: "P",
    None: "N",
    Low: "L",
    Medium: "M",
    High: "H",
    Single: "S",
    Multiple: "M",
    Present: "P",
    Required: "R",
    Passive: "P",
    Active: "A",
    Unchanged: "U",
    Changed: "C",
    Partial: "P",
    Complete: "C",
};
// Metric order is part of the vector's shape, so each version lists its own
// components in spec order, paired with the label they arrive under.
const VECTOR_COMPONENTS = {
    v2: [
        ["AV", "access vector"],
        ["AC", "access complexity"],
        ["Au", "authentication"],
        ["C", "confidentiality impact"],
        ["I", "integrity impact"],
        ["A", "availability impact"],
    ],
    v3: [
        ["AV", "attack vector"],
        ["AC", "attack complexity"],
        ["PR", "privileges required"],
        ["UI", "user interaction"],
        ["S", "scope"],
        ["C", "confidentiality impact"],
        ["I", "integrity impact"],
        ["A", "availability impact"],
    ],
    v4: [
        ["AV", "attack vector"],
        ["AC", "attack complexity"],
        ["AT", "attack requirements"],
        ["PR", "privileges required"],
        ["UI", "user interaction"],
        ["VC", "confidentiality impact (vulnerable system)"],
        ["VI", "integrity impact (vulnerable system)"],
        ["VA", "availability impact (vulnerable system)"],
        ["SC", "confidentiality impact (subsequent system)"],
        ["SI", "integrity impact (subsequent system)"],
        ["SA", "availability impact (subsequent system)"],
    ],
};
/** Rebuilds the canonical vector string the metrics were decoded from, e.g.
 * `CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H`. Returns `undefined` when no
 * component can be resolved; v2 vectors carry no `CVSS:` prefix, which is the
 * notation that version defines. */
export function formatCvssVector(cvss) {
    const get = metricLookup(cvss);
    const group = specGroup(cvss);
    const components = VECTOR_COMPONENTS[group]
        .map(([abbreviation, label]) => {
        var _a;
        const code = VALUE_CODES[(_a = get(label)) !== null && _a !== void 0 ? _a : ""];
        return code ? `${abbreviation}:${code}` : undefined;
    })
        .filter(Boolean);
    if (!components.length)
        return undefined;
    return `${vectorPrefix(cvss, group)}${components.join("/")}`;
}
// v2 defines no `CVSS:` prefix. Findings arrive with an unqualified major version
// (`3`), and since v3.0 and v3.1 share every metric, fragment and base score, an
// unqualified v3 is spelled as the current minor rather than the superseded 3.0.
const vectorPrefix = ({ version }, group) => {
    if (group === "v2")
        return "";
    return `CVSS:${(version === 3 ? 3.1 : version).toFixed(1)}/`;
};
//# sourceMappingURL=cvss.js.map